/**
 * O "palco" onde o widget é montado. Usado pela tela de preview.
 *
 * Existe para não haver duas rotinas de montagem no painel — o ciclo de vida (montar, destruir,
 * remontar sob comando) fica num lugar só, e monta o `mountWidget` do próprio SDK.
 *
 * Três detalhes de CSS que este componente resolve, todos causa de widget cortado no preview:
 *
 * 1. **Containing block.** O widget se posiciona com `position: fixed`, que se ancora na viewport.
 *    `position: relative` no pai não o segura; um `transform` sim.
 *
 * 2. **Unidades de viewport.** A janela de chat usa `min(370px, calc(100vw - …))` e
 *    `min(640px, calc(100vh - …))`. Dentro de um palco, essas unidades continuam valendo a janela do
 *    navegador inteira. Quem resolve isso hoje é o **widget**: ele mede o `container` que recebe e
 *    publica `--merlin-viewport-*` no próprio host (ver `publicarViewport` no SDK). Com `fit`, o palco
 *    declara as mesmas propriedades na caixa virtual — redundante por construção, e de propósito: se
 *    a medição do widget falhar (sem `ResizeObserver`, primeiro frame), a declaração do palco ainda
 *    vale por herança em vez de cair no `100vw` do navegador.
 *
 * 3. **Recorte.** O palco virtual é maior que o espaço disponível e precisa ser reduzido. A
 *    centralização é feita com `translate(-50%, -50%)` sobre posicionamento absoluto, e **não** com
 *    `place-items: center`: `overflow` recorta pela caixa de *layout*, que continua no tamanho
 *    original mesmo com `transform: scale` (o transform é só visual), e o recorte de conteúdo
 *    centralizado que transborda tem comportamento que varia entre navegadores. Com `translate`, a
 *    caixa visual fica sempre centrada e a escala garante que cabe.
 *
 * A escala começa como `null`, não como 1: enquanto não há medição, o palco fica invisível em vez de
 * aparecer em tamanho cheio e ser recortado. Sem isso, uma medição que devolvesse 0 na primeira
 * passada travaria a escala em 1 para sempre — que era exatamente o defeito do topo cortado.
 */

import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { mountWidget, type WidgetEvent, type WidgetInstance } from '@merlin/sdk';
import type { WebchatConfig } from '@merlin/flow-engine';

export interface StageFit {
  width: number;
  height: number;
}

/** Folga entre a moldura e as paredes do container, para ela não encostar nas bordas. */
const STAGE_PADDING = 16;

export interface WidgetStageProps {
  config: WebchatConfig;
  /** Trocar este número remonta a conversa. Evita reiniciar o fluxo a cada tecla digitada. */
  generation: number;
  autoOpen?: boolean;
  className?: string;
  /** Viewport simulada. Sem isto, o palco usa o tamanho real do container. */
  fit?: StageFit;
  /** Classes aplicadas à caixa virtual — é onde vai a moldura de celular, por exemplo. */
  stageClassName?: string;
  /** Fundo desenhado dentro do palco escalado (o site fictício, por exemplo). */
  children?: ReactNode;
  onEvent?(event: WidgetEvent, instance: WidgetInstance): void;
  /** Escala aplicada, para o pai poder informar que a moldura está reduzida. `null` = ainda medindo. */
  onScaleChange?(scale: number | null): void;
}

/**
 * Fator para o palco virtual caber na caixa disponível, sem nunca ampliar.
 * `null` enquanto não foi possível medir — quem chama usa isso para não pintar nada torto.
 */
function useFitScale(
  outerRef: React.RefObject<HTMLDivElement | null>,
  fit: StageFit | undefined,
): number | null {
  const [scale, setScale] = useState<number | null>(null);

  useLayoutEffect(() => {
    const outer = outerRef.current;
    if (!fit) {
      setScale(1);
      return;
    }
    /*
     * Invalida a medição anterior antes de medir de novo. Sem isto, trocar de Computador (escala 1)
     * para Celular manteria o 1 herdado caso a nova medição falhasse — de volta ao palco maior que o
     * container e recortado, que é justamente o defeito que a escala `null` existe para evitar.
     */
    setScale(null);
    if (!outer) return;

    const measure = (): void => {
      const width = outer.clientWidth - STAGE_PADDING * 2;
      const height = outer.clientHeight - STAGE_PADDING * 2;
      if (width <= 0 || height <= 0) return;
      setScale(Math.min(1, width / fit.width, height / fit.height));
    };

    measure();

    // jsdom não implementa ResizeObserver: sem ele, a medição única do mount é o que há.
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(outer);
    return () => observer.disconnect();
  }, [outerRef, fit?.width, fit?.height]);

  return scale;
}

export function WidgetStage({
  config,
  generation,
  autoOpen = true,
  className = '',
  fit,
  stageClassName = '',
  children,
  onEvent,
  onScaleChange,
}: WidgetStageProps) {
  const outerRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  // Refs para config e callback: nas deps do efeito, cada edição remontaria a conversa.
  const configRef = useRef(config);
  const onEventRef = useRef(onEvent);
  configRef.current = config;
  onEventRef.current = onEvent;

  const scale = useFitScale(outerRef, fit);
  const onScaleRef = useRef(onScaleChange);
  onScaleRef.current = onScaleChange;

  useEffect(() => {
    onScaleRef.current?.(scale);
  }, [scale]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let instance: WidgetInstance | null = null;
    instance = mountWidget({
      config: configRef.current,
      container,
      mode: 'preview',
      autoOpen,
      onEvent: (event) => {
        if (instance) onEventRef.current?.(event, instance);
      },
    });

    return () => {
      instance?.destroy();
      instance = null;
    };
  }, [generation, autoOpen]);

  if (!fit) {
    return (
      <div ref={outerRef} className={className}>
        {children}
        <div ref={containerRef} className="absolute inset-0" style={{ transform: 'translateZ(0)' }} />
      </div>
    );
  }

  return (
    <div ref={outerRef} className={`relative overflow-hidden ${className}`}>
      <div
        className={`absolute overflow-hidden bg-white ${stageClassName}`}
        style={{
          width: fit.width,
          height: fit.height,
          left: '50%',
          top: '50%',
          // `translate` centra a caixa visual; `scale` a encaixa; `translateZ` garante o containing
          // block do `position: fixed` que o widget usa.
          transform: `translate(-50%, -50%) scale(${scale ?? 1}) translateZ(0)`,
          // Invisível até haver medição: melhor nada do que a moldura cortada por um frame.
          visibility: scale === null ? 'hidden' : 'visible',
          // As duas propriedades atravessam o Shadow DOM e dizem ao widget que tela assumir.
          ['--merlin-viewport-width' as string]: `${fit.width}px`,
          ['--merlin-viewport-height' as string]: `${fit.height}px`,
        }}
      >
        {children}
        <div ref={containerRef} className="absolute inset-0" />
      </div>
    </div>
  );
}
